import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DataTableApiId } from '@/model/DataTable.model';
import { ID } from '@/model/Common.model';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { isApiId } from '@/utils/ApiId.utils';
import { Study } from '@/model/Study.model';
import { DataTableService } from '@/components/UI/DataTableV2/service/DataTableService';
import { _notNil } from '@/littledash';
import * as Auth from '@/support/auth';
import { DataTableApiService } from '@/components/UI/DataTableV2/service/DataTableApiService';
import http from '@/support/http';
import { DataTableService as IDataTableService } from '@/components/UI/DataTableV2/DataTable.model';
import type { StudyUserSummaryV1, UserApiId } from '@/model/User.model';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import axios from 'axios';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { TeamGlossaryApiId } from '@/model/Glosary.model';
import { getColumnContextMetrics } from '@/components/Studies/DataTables/Display/DisplayDatatable.utils';
import { PresetCalculation } from '@/model/PresetCalculation.model';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';

const getUserFriendlyErrorMessage = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
      return 'Network error: Unable to connect to server. Please check your connection and try again.';
    }
    if (err.response?.status === 401) {
      return 'Authentication failed. Please log in again.';
    }
    if (err.response?.status === 403) {
      return 'Permission denied. You may not have access to this DataTable.';
    }
    if (err.response?.status === 404) {
      return 'The DataTable could not be found. It may have been deleted.';
    }
    if (err.response?.status && err.response.status >= 500) {
      return 'Server error occurred. Please try again or contact support.';
    }
  }

  return 'An unexpected error occurred loading the DataTable. Please refresh or contact support.';
};

type InitializedDataTableContext = {
  type: 'initialized';
  studyApiId: string;
  dataTableApiId: string;
  dataTableService: IDataTableService;
  metrics: Array<PresetCalculation>;
  observations: Array<{ title?: string; api_id: TeamGlossaryApiId }>;
  users: ReadonlyMap<UserApiId, StudyUserSummaryV1>;
};

type UninitializedDataTableContext = {
  type: 'uninitialized';
};

type ErrorDataTableContext = {
  type: 'error';
  message: string;
};

type DataTableContext = InitializedDataTableContext | UninitializedDataTableContext | ErrorDataTableContext;

const context = createContext<DataTableContext>({ type: 'uninitialized' });

export type DataTableProviderProps = { studyId: ID; dataTableApiId: DataTableApiId };
const featureSelector = createSelector([featuresSelector], (features) => ({
  realtimeEnabled: features?.datatables_realtime_events ?? false,
}));
export const useDataTable = (): InitializedDataTableContext => {
  const dataTableContext = useContext(context);
  if (dataTableContext.type === 'initialized') {
    return dataTableContext;
  }
  throw new InVivoError('DataTable context not initialized', { slug: 'data-table-not-initialized' });
};

export const DataTableProvider: FC<PropsWithChildren<DataTableProviderProps>> = ({
  studyId,
  dataTableApiId,
  children,
}) => {
  const [contextValue, setContextValue] = useState<DataTableContext>({ type: 'uninitialized' });
  const { realtimeEnabled } = useSelector(featureSelector);
  const { response: studyLoadResponse } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.show.p',
    path: { id: studyId },
    options: { invokeOnInit: true, onError: { slug: 'datatable-study-load' } },
  });

  const { response: observationsResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'observations',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
    },
    options: {
      onError: { toast: false, slug: 'datatable-observations-glossary-load' },
    },
  });

  const study = studyLoadResponse?.type === 'success' ? (studyLoadResponse.body as { data: Study }).data : undefined;
  const { response: studyUsersResponse, invoke: loadStudyUsers } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/users',
    path: { studyId: study?.api_id as Study['api_id'] },
    query: { perPage: -1, status: ['active'] },
    options: {
      invokeOnInit: false,
      onError: { toast: false, slug: 'datatable-study-users-load' },
    },
  });

  useEffect(() => {
    if (_notNil(study)) {
      loadStudyUsers({ path: { studyId: study.api_id }, query: { perPage: -1, status: ['active'] } });
    }
  }, [study?.api_id]);

  useEffect(() => {
    if (
      studyLoadResponse?.type === 'success' &&
      (observationsResponse?.type === 'success' || observationsResponse?.type === 'error') &&
      (studyUsersResponse?.type === 'success' || studyUsersResponse?.type === 'error') &&
      _notNil(study) &&
      isApiId('dtl', dataTableApiId)
    ) {
      const studyUsers = studyUsersResponse.type === 'success' ? studyUsersResponse.body.data : [];
      const usersMap: ReadonlyMap<UserApiId, StudyUserSummaryV1> = new Map(studyUsers.map((u) => [u.api_id, u]));
      const observations =
        observationsResponse.type === 'success'
          ? observationsResponse.body.data.map((observation) => ({
              title: observation.title ?? undefined,
              api_id: observation.api_id,
            }))
          : [];

      const dataTableService = new DataTableService({
        readonly: !Auth.isWriteUserForStudyWithUsers(study, studyUsers),
        apiService: new DataTableApiService({ dataTableId: dataTableApiId, studyId: study.api_id, axios: http }),
        studyApiId: study.api_id,
        dataTableApiId,
        realtimeEnabled,
        rowHeaders: [
          { id: 'animalName', title: 'Name', displayed: true },
          {
            id: 'animalStudyGroup',
            title: 'Study Group',
            displayed: true,
          },
          { id: 'animalCage', title: 'Cage', displayed: false },
          {
            id: 'animalTail',
            title: 'Tail',
            displayed: true,
          },
          {
            id: 'animalEar',
            title: 'Ear',
            displayed: false,
          },
          {
            id: 'animalTag',
            title: 'Tag',
            displayed: false,
          },
          {
            id: 'animalDonor',
            title: 'Donor',
            displayed: false,
          },
          {
            id: 'animalLivingStatus',
            title: 'Living Status',
            displayed: false,
          },
        ],
        pinnedColumns: [],
        dimensions: {
          cellWidth: 150,
          cellHeight: 27,
          columnHeight: 40,
        },
      });
      dataTableService
        .initialise()
        .then((dtlService) => {
          setContextValue({
            type: 'initialized',
            studyApiId: study.api_id,
            dataTableApiId,
            dataTableService: dtlService,
            metrics: getColumnContextMetrics(study.settings.calculations ?? []),
            observations: observations,
            users: usersMap,
          });
        })
        .catch((err) => {
          const error = new InVivoError('Failed to initialize DataTableService', {
            cause: err,
            slug: 'data-table-service-init',
          });
          ExceptionHandler.captureException(error);
          setContextValue({
            type: 'error',
            message: getUserFriendlyErrorMessage(err),
          });
        });
      return () => {
        dataTableService.destroy();
      };
    }
  }, [studyLoadResponse, observationsResponse, studyUsersResponse, dataTableApiId, realtimeEnabled]);

  if (contextValue.type === 'initialized') {
    return <context.Provider value={contextValue}>{children}</context.Provider>;
  }

  if (contextValue.type === 'error') {
    return (
      <div className="flex flex-column items-center justify-center pa4" style={{ minHeight: '400px' }}>
        <div className="ba b--red br2 pa3 bg-washed-red red" style={{ maxWidth: '600px' }}>
          <h3 className="mt0 mb2">Failed to Load DataTable</h3>
          <p className="ma0">{contextValue.message}</p>
        </div>
      </div>
    );
  }

  return null;
};
